/**
 * CreateTaskModal — Dashboard Slice E. The "+ New task" form: pick what
 * the task is about (Firm / User / Misc), pick who must do it, write
 * the instruction, optionally set a due date and an opt-in Slack ping
 * for the assignee.
 *
 * v1 scope (locked):
 *   - No Lead kind in the picker. Leads have no list/search endpoint
 *     today; lead-about to-dos can still be created from the Operations
 *     Surface's existing flow (which fires the same POST route).
 *     #541 nuance: a LEAD-bound `initialAbout` (the Pinned card's "+ To-do")
 *     is accepted as a LOCKED binding — the kind toggle and pickers are
 *     replaced by a read-only Matter row (`initialAboutLabel`), because the
 *     caller already knows exactly which matter the to-do is about.
 *   - Firm / User pickers use the existing `/api/firm-success/firms`
 *     and `/api/firm-success/users` payloads (small, polled rosters).
 *     Client-side filter on a single text input.
 *   - Misc collapses to a free-text Reference (Slack URL, "Text from me
 *     re: X", reminder-to-self).
 *   - Assignee dropdown reads OPERATOR_ROSTER — every entry on it, no fixed
 *     count. Do not re-spell the names in this comment; that copy goes
 *     stale silently (#708). To-dos assigned to a roster-but-not-
 *     allowlisted operator sit waiting until the sign-in allowlist
 *     re-opens.
 *   - notifyOnSlack checkbox is visible only when assignee !== self —
 *     pinging yourself is meaningless, and the server enforces the same
 *     gate before enqueueing the `task-assigned` notification.
 *
 * On success: invalidates ["dashboard"] + ["dashboard-count"] (the
 * useCreateTask hook does this); closes the modal; resets state.
 */

import { useEffect, useMemo, useState } from "react";
import {
  TASK_PRIORITIES,
  type CreateTaskRequest,
  type TaskAbout,
  type TaskPriority,
} from "@command-center/shared";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useClientSuccessFirms } from "@/lib/use-client-success-firms";
import { useClientSuccessUsers } from "@/lib/use-client-success-users";
import { OPERATORS } from "@/lib/operators";
import { useCreateTask } from "@/lib/use-surface-tasks";

export type AboutKind = "firm" | "user" | "misc";

const KIND_LABELS: Record<AboutKind, string> = {
  firm: "Firm",
  user: "User",
  misc: "Misc",
};

const KINDS: readonly AboutKind[] = ["firm", "user", "misc"];

/**
 * Derive initial form state from an `initialAbout` pre-bind. Splits the
 * polymorphic shape back into the modal's per-field state so the kind toggle
 * + the relevant picker land on the right values. A null pre-bind hands back
 * the empty defaults (the modal's pre-Slice-I behaviour).
 */
function initialStateFromAbout(initialAbout: TaskAbout | null): {
  kind: AboutKind;
  firmId: string;
  userId: string;
  reference: string;
} {
  if (initialAbout === null) {
    return { kind: "firm", firmId: "", userId: "", reference: "" };
  }
  switch (initialAbout.kind) {
    case "firm":
      return { kind: "firm", firmId: initialAbout.id, userId: "", reference: "" };
    case "user":
      return { kind: "user", firmId: "", userId: initialAbout.id, reference: "" };
    case "misc":
      return {
        kind: "misc",
        firmId: "",
        userId: "",
        reference: initialAbout.reference,
      };
    case "lead":
      // A lead pre-bind is the LOCKED path (#541): `leadBound` bypasses this
      // state entirely — the `about` useMemo returns `initialAbout` and the
      // kind toggle/pickers never render — so these are inert placeholders.
      return { kind: "firm", firmId: "", userId: "", reference: "" };
  }
}

export function CreateTaskModal({
  open,
  onClose,
  viewerEmail,
  initialAbout = null,
  initialAboutLabel = null,
}: {
  open: boolean;
  onClose: () => void;
  viewerEmail: string;
  /**
   * Pre-bind the form to a specific entity (Slice I — the `+ Ask` button on
   * the Firm / User Detail Panels passes this so the operator skips the
   * picker entirely). When set, the kind toggle still renders but the
   * picker for the bound kind shows the entity as already-selected. Null is
   * the global `+ New task` posture — operator picks everything.
   *
   * #541: a `kind: "lead"` pre-bind is a LOCKED binding — no kind toggle, no
   * pickers, just a read-only Matter row (see `initialAboutLabel`).
   */
  initialAbout?: TaskAbout | null;
  /** Display label for a locked lead pre-bind (the matter's name). */
  initialAboutLabel?: string | null;
}) {
  const leadBound = initialAbout?.kind === "lead";
  const initial = initialStateFromAbout(initialAbout);
  const [kind, setKind] = useState<AboutKind>(initial.kind);
  const [firmId, setFirmId] = useState<string>(initial.firmId);
  const [userId, setUserId] = useState<string>(initial.userId);
  const [reference, setReference] = useState<string>(initial.reference);
  const [assignee, setAssignee] = useState<string>(viewerEmail);
  const [instruction, setInstruction] = useState<string>("");
  const [dueBy, setDueBy] = useState<string>("");
  const [priority, setPriority] = useState<TaskPriority>("Normal");
  const [notifyOnSlack, setNotifyOnSlack] = useState<boolean>(false);

  const createTask = useCreateTask();

  const reset = () => {
    const next = initialStateFromAbout(initialAbout);
    setKind(next.kind);
    setFirmId(next.firmId);
    setUserId(next.userId);
    setReference(next.reference);
    setAssignee(viewerEmail);
    setInstruction("");
    setDueBy("");
    setPriority("Normal");
    setNotifyOnSlack(false);
    createTask.reset();
  };

  const close = () => {
    if (createTask.isPending) return;
    reset();
    onClose();
  };

  const about: TaskAbout | null = useMemo(() => {
    if (leadBound) return initialAbout;
    if (kind === "firm") return firmId ? { kind: "firm", id: firmId } : null;
    if (kind === "user") return userId ? { kind: "user", id: userId } : null;
    return reference.trim() ? { kind: "misc", reference: reference.trim() } : null;
  }, [leadBound, initialAbout, kind, firmId, userId, reference]);

  const assigneeIsSelf =
    assignee.trim().toLowerCase() === viewerEmail.trim().toLowerCase();

  const canSubmit =
    about !== null &&
    assignee.trim() !== "" &&
    instruction.trim() !== "" &&
    !createTask.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || about === null) return;
    const body: CreateTaskRequest = {
      kind: "todo",
      about,
      assignee,
      instruction: instruction.trim(),
      dueBy: dueBy || undefined,
      priority,
      notifyOnSlack: notifyOnSlack && !assigneeIsSelf,
    };
    try {
      await createTask.mutateAsync(body);
      reset();
      onClose();
    } catch {
      // Error surfaces via createTask.isError below; keep the modal open.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      labelledBy="create-task-title"
      describedBy="create-task-description"
      testId="create-task-modal"
    >
      <DialogHeader>
        <DialogTitle id="create-task-title">+ New task</DialogTitle>
        <DialogDescription id="create-task-description">
          {leadBound
            ? "Create a to-do attached to this matter."
            : "Create a to-do attached to a firm, a user, or a free-text reference."}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {leadBound ? (
          <Field label="Matter" htmlFor="create-task-matter">
            <div
              id="create-task-matter"
              className="rounded border border-input bg-muted/40 px-2 py-1 text-sm"
              data-testid="create-task-matter"
            >
              {initialAboutLabel ?? "This matter"}
            </div>
          </Field>
        ) : (
          <>
            <KindToggle value={kind} onChange={setKind} />

            {kind === "firm" ? (
              <FirmPicker value={firmId} onChange={setFirmId} />
            ) : null}
            {kind === "user" ? (
              <UserPicker value={userId} onChange={setUserId} />
            ) : null}
            {kind === "misc" ? (
              <ReferenceInput value={reference} onChange={setReference} />
            ) : null}
          </>
        )}

        <Field label="Assignee" htmlFor="create-task-assignee">
          <select
            id="create-task-assignee"
            className="block w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            data-testid="create-task-assignee"
          >
            {OPERATORS.map((o) => (
              <option key={o.email} value={o.email}>
                {o.name} ({o.email})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Instruction" htmlFor="create-task-instruction">
          <Textarea
            id="create-task-instruction"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="What needs to happen?"
            rows={3}
            data-testid="create-task-instruction"
          />
        </Field>

        <Field label="Due by" htmlFor="create-task-due-by" optional>
          <Input
            id="create-task-due-by"
            type="date"
            value={dueBy}
            onChange={(e) => setDueBy(e.target.value)}
            data-testid="create-task-due-by"
          />
        </Field>

        <Field label="Priority" htmlFor="create-task-priority">
          <select
            id="create-task-priority"
            className="block w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            data-testid="create-task-priority"
          >
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>

        {!assigneeIsSelf ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={notifyOnSlack}
              onChange={(e) => setNotifyOnSlack(e.target.checked)}
              data-testid="create-task-notify-on-slack"
            />
            <span>Ping the assignee on Slack</span>
          </label>
        ) : null}

        {createTask.isError ? (
          <div
            className="rounded border border-error-border bg-error-bg p-2 text-xs text-error"
            data-testid="create-task-error"
          >
            Couldn't create the task. {String(createTask.error)}
          </div>
        ) : null}
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={close}
          disabled={createTask.isPending}
          data-testid="create-task-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="create-task-submit"
        >
          {createTask.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label
        htmlFor={htmlFor}
        className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        {label}
        {optional ? (
          <span className="ml-1 normal-case text-muted-foreground/70">
            (optional)
          </span>
        ) : null}
      </label>
      {children}
    </div>
  );
}

function KindToggle({
  value,
  onChange,
}: {
  value: AboutKind;
  onChange: (next: AboutKind) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="What is this task about?"
      className="inline-flex rounded border border-input bg-muted/40 p-0.5"
      data-testid="create-task-kind"
    >
      {KINDS.map((k) => (
        <button
          key={k}
          type="button"
          role="radio"
          aria-checked={value === k}
          onClick={() => onChange(k)}
          data-testid={`create-task-kind-${k}`}
          className={
            "rounded px-3 py-1 text-xs font-medium " +
            (value === k
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground")
          }
        >
          {KIND_LABELS[k]}
        </button>
      ))}
    </div>
  );
}

function FirmPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const query = useClientSuccessFirms();
  const [filter, setFilter] = useState("");
  const firms = query.data?.firms ?? [];
  const matches = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return firms;
    return firms.filter((f) => f.name.toLowerCase().includes(q));
  }, [firms, filter]);

  // Auto-select when the roster narrows to a single match — typing a name
  // until one firm remains picks it, so the operator skips the extra
  // open-the-dropdown step. The value guard stops the effect re-firing.
  const onlyMatch = matches.length === 1 ? matches[0] : undefined;
  useEffect(() => {
    if (onlyMatch && value !== onlyMatch.id) {
      onChange(onlyMatch.id);
    }
  }, [onlyMatch, value, onChange]);

  return (
    <Field label="Firm" htmlFor="create-task-firm-filter">
      <Input
        id="create-task-firm-filter"
        type="text"
        placeholder="Search firms…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="create-task-firm-filter"
      />
      <select
        className="mt-2 block w-full rounded border border-input bg-background px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="create-task-firm-select"
        aria-label="Firm"
      >
        <option value="">Select a firm…</option>
        {query.isLoading ? <option disabled>Loading…</option> : null}
        {!query.isLoading && matches.length === 0 ? (
          <option disabled>No firms match.</option>
        ) : null}
        {matches.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </select>
    </Field>
  );
}

function UserPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const query = useClientSuccessUsers();
  const [filter, setFilter] = useState("");
  const users = query.data?.users ?? [];
  const matches = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.firmName ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q),
    );
  }, [users, filter]);

  // Auto-select when the roster narrows to a single match — same affordance
  // as the firm picker. The value guard stops the effect re-firing.
  const onlyMatch = matches.length === 1 ? matches[0] : undefined;
  useEffect(() => {
    if (onlyMatch && value !== onlyMatch.id) {
      onChange(onlyMatch.id);
    }
  }, [onlyMatch, value, onChange]);

  return (
    <Field label="User" htmlFor="create-task-user-filter">
      <Input
        id="create-task-user-filter"
        type="text"
        placeholder="Search users by name, firm, or email…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="create-task-user-filter"
      />
      <select
        className="mt-2 block w-full rounded border border-input bg-background px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="create-task-user-select"
        aria-label="User"
      >
        <option value="">Select a user…</option>
        {query.isLoading ? <option disabled>Loading…</option> : null}
        {!query.isLoading && matches.length === 0 ? (
          <option disabled>No users match.</option>
        ) : null}
        {matches.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
            {u.firmName ? ` — ${u.firmName}` : ""}
          </option>
        ))}
      </select>
    </Field>
  );
}

function ReferenceInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <Field label="Reference" htmlFor="create-task-reference">
      <Input
        id="create-task-reference"
        type="text"
        placeholder='e.g. "Slack: …" or "Text from me re: X"'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="create-task-reference"
      />
    </Field>
  );
}
